import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { currencies, unitsMeasure } from '../utils/dropdownOptions';
import { useEffect, useState } from 'react';
import { GetCountries } from 'react-country-state-city';
import { FormSectionWrapper } from './FormSectionWrapper';
import { nanoid } from 'nanoid';
import commodityData from '../utils/commodityData.json';

const initialFormData = {
    name: '',
    unitPrice: {
        amount: 0,
        currency: ''
    },
    quantity: 1,
    quantityUnits: '',
    customsValue: {
        amount: 0,
        currency: ''
    },
    countryOfManufacture: '',
    description: '',
    weight: {
        units: '',
        value: 0
    },
    exportLicenseNumber: '',
    exportLicenseExpirationDate: '',
    purpose: '',
    partNumber: '',
    cIMarksAndNumbers: '',
    harmonizedCode: ''
}

export const Commodities = ({ formik, isReturnShipmentTab }) => {
    const commodities = isReturnShipmentTab ? formik.values.returnData.commodities : formik.values.commodities;
    const commoditiesField = isReturnShipmentTab ? 'returnData.commodities' : 'commodities';
    const [countries, setCountries] = useState([]);
    const [formData, setFormData] = useState(initialFormData);
    const [selectedCommodityIds, setSelectedCommodityIds] = useState([]);

    useEffect(() => {
        const getCountries = async () => {
            const countries = await GetCountries();
            setCountries(countries);
        }

        getCountries();
    }, [])

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'quantity') {
            setFormData({
                ...formData,
                [name]: value,
                customsValue: {
                    ...formData.customsValue,
                    amount: formData.unitPrice.amount * value
                }
            });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    }

    const handleNestedChange = (e, key) => {
        const { name, value } = e.target;
        const obj = { ...formData[key], [name]: value };
        setFormData({ ...formData, [key]: obj });
    }

    const handleUnitPriceChange = (e) => {
        const { name, value } = e.target;
        const updatedUnitPrice = { ...formData.unitPrice, [name]: value };
        const newCustomsVal = name === 'amount' ? value * formData.quantity : value;
        const updatedCustomsValue = { ...formData.customsValue, [name]: newCustomsVal }
        setFormData({
            ...formData,
            unitPrice: updatedUnitPrice,
            customsValue: updatedCustomsValue
        });
    }

    const handleCommoditySelection = (e) => {
        const commodityCode = e.target.value;
        if (commodityCode === '') {
            return setFormData(initialFormData);
        }
        const commodity = commodityData.find((c) => c.Code === commodityCode);
        let commodityPurpose;
        if (commodity.CommPurpose === 'Consumer_Use') {
            commodityPurpose = 'CONSUMER';
        } else if (commodity.CommPurpose === 'Business_Use') {
            commodityPurpose = 'BUSINESS';
        } else {
            commodityPurpose = '';
        }
        setFormData({
            name: commodityCode,
            unitPrice: {
                amount: commodity.UnitValue,
                currency: commodity.CurrencyCode
            },
            quantity: commodity.Quantity,
            quantityUnits: commodity.UnitOfMeasure,
            customsValue: {
                amount: commodity.UnitValue * commodity.Quantity,
                currency: commodity.CurrencyCode
            },
            countryOfManufacture: commodity.Country,
            description: commodity.Desc,
            weight: {
                units: 'LB',
                value: commodity.Weight
            },
            exportLicenseNumber: '',
            exportLicenseExpirationDate: '',
            purpose: commodityPurpose,
            partNumber: commodity.PartNum,
            cIMarksAndNumbers: commodity.MarkNum,
            harmonizedCode: commodity.HmnzdCode
        })
    }

    const addToShipment = () => {
        const newCommodity = { ...formData, id: nanoid() };
        const num = +newCommodity.weight.value;
        newCommodity.weight.value = +num.toFixed(2);
        formik.setFieldValue(commoditiesField, [...commodities, newCommodity]);
        setFormData(initialFormData);
    }

    const handleCheckChange = (e) => {
        const { value, checked } = e.target;
        if (selectedCommodityIds.length === 0) {
            const selectedCommodity = commodities.find((i) => i.id === value);
            setFormData(selectedCommodity);
        } else {
            setFormData(initialFormData);
        }
        if (checked) {
            setSelectedCommodityIds([...selectedCommodityIds, value]);
        } else {
            setSelectedCommodityIds(selectedCommodityIds.filter((i) => i !== value));
        }
    }

    const removeCommodity = () => {
        formik.setFieldValue(commoditiesField, commodities.filter((i) => !selectedCommodityIds.includes(i.id)));
        setSelectedCommodityIds([]);
        setFormData(initialFormData);
    }

    const updateCommodity = () => {
        const selectedId = selectedCommodityIds[0];
        formik.setFieldValue(commoditiesField, commodities.map((i) => (
            i.id === selectedId ? { ...formData, id: selectedId } : i
        )));
        setFormData(initialFormData);
        setSelectedCommodityIds([]);
    }

    return (
        <div className='form-grid'>
            <div>
                <FormSectionWrapper title='Add a commodity'>
                    <Form.Group className='form-group'>
                        <Form.Label>Commodity ID</Form.Label>
                        <Form.Select
                            value={formData.name}
                            onChange={handleCommoditySelection}
                        >
                            <option value=''>Select an option</option>
                            {
                                commodityData.map((c) => (
                                    <option key={c.Code} value={c.Code}>{c.Code}</option>
                                ))
                            }
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Commodity description</Form.Label>
                        <Form.Control
                            as='textarea'
                            rows={2}
                            name='description'
                            value={formData.description}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Country of manufacture</Form.Label>
                        <Form.Select
                            name='countryOfManufacture'
                            value={formData.countryOfManufacture}
                            onChange={handleChange}
                        >
                            <option value=''>Select an option</option>
                            {
                                countries.map((c) => (
                                    <option key={c.id} value={c.iso2}>{c.iso2} - {c.name}</option>
                                ))
                            }
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Quantity / Unit of measure</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type='number'
                                onWheel={(e) => e.target.blur()}
                                name='quantity'
                                value={formData.quantity}
                                onChange={handleChange}
                            />
                            <Form.Select
                                name='quantityUnits'
                                value={formData.quantityUnits}
                                onChange={handleChange}
                            >
                                <option value=''>Select an option</option>
                                {
                                    unitsMeasure.map((i) => (
                                        <option key={i.value} value={i.value}>{i.value} - {i.description}</option>
                                    ))
                                }
                            </Form.Select>
                        </InputGroup>
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Unit value</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type='number'
                                onWheel={(e) => e.target.blur()}
                                name='amount'
                                value={formData.unitPrice.amount}
                                onChange={handleUnitPriceChange}
                            />
                            <Form.Select
                                name='currency'
                                value={formData.unitPrice.currency}
                                onChange={handleUnitPriceChange}
                            >
                                <option value=''>Select an option</option>
                                {
                                    currencies.map((i) => (
                                        <option key={i.code} value={i.code}>{i.code} - {i.currency}</option>
                                    ))
                                }
                            </Form.Select>
                        </InputGroup>
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Total customs value</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type='number'
                                onWheel={(e) => e.target.blur()}
                                name='amount'
                                value={formData.customsValue.amount}
                                onChange={(e) => handleNestedChange(e, 'customsValue')}
                            />
                            <Form.Select
                                name='currency'
                                value={formData.customsValue.currency}
                                onChange={(e) => handleNestedChange(e, 'customsValue')}
                            >
                                <option value=''>Select an option</option>
                                {
                                    currencies.map((i) => (
                                        <option key={i.code} value={i.code}>{i.code} - {i.currency}</option>
                                    ))
                                }
                            </Form.Select>
                        </InputGroup>
                    </Form.Group>
                </FormSectionWrapper>
                <FormSectionWrapper title='Additional commodity information'>
                    <Form.Group className='form-group'>
                        <Form.Label>Total commodity weight</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type='number'
                                onWheel={(e) => e.target.blur()}
                                name='value'
                                value={formData.weight.value}
                                onChange={(e) => handleNestedChange(e, 'weight')}
                            />
                            <Form.Select
                                name='units'
                                value={formData.weight.units}
                                onChange={(e) => handleNestedChange(e, 'weight')}
                            >
                                <option value=''>Select an option</option>
                                <option value='LB'>lbs</option>
                                <option value='KG'>kgs</option>
                            </Form.Select>
                        </InputGroup>
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Harmonized code</Form.Label>
                        <Form.Control
                            type='text'
                            name='harmonizedCode'
                            value={formData.harmonizedCode}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Part number</Form.Label>
                        <Form.Control
                            type='text'
                            name='partNumber'
                            value={formData.partNumber}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Marks/Numbers</Form.Label>
                        <Form.Control
                            type='text'
                            name='cIMarksAndNumbers'
                            value={formData.cIMarksAndNumbers}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Commodity purpose</Form.Label>
                        <Form.Select
                            name='purpose'
                            value={formData.purpose}
                            onChange={handleChange}
                        >
                            <option value=''>Select an option</option>
                            <option value='BUSINESS'>Business</option>
                            <option value='CONSUMER'>Consumer</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Export license number / Expiration date</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type='text'
                                name='exportLicenseNumber'
                                value={formData.exportLicenseNumber}
                                onChange={handleChange}
                            />
                            <Form.Control
                                type='date'
                                name='exportLicenseExpirationDate'
                                value={formData.exportLicenseExpirationDate}
                                onChange={handleChange}
                            />
                        </InputGroup>
                    </Form.Group>
                    <br />
                    {selectedCommodityIds.length > 0 ?
                        <Button onClick={updateCommodity} disabled={selectedCommodityIds.length > 1} variant='secondary'>Update</Button>
                        :
                        <Button onClick={addToShipment} variant='secondary'>Add to shipment</Button>
                    }
                </FormSectionWrapper>
            </div>
            <div>
                <FormSectionWrapper title='Commodity summary'>
                    <Table>
                        <thead>
                            <tr>
                                <th>Select</th>
                                <th>Quantity</th>
                                <th>Unit of measure</th>
                                <th>Commodity ID</th>
                                <th>Commodity description</th>
                                <th>Country of manufacture</th>
                                <th>Unit value</th>
                                <th>Total customs value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                commodities.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <Form.Check
                                                type='checkbox'
                                                onChange={handleCheckChange}
                                                value={c.id}
                                                checked={selectedCommodityIds.includes(c.id)}
                                            />
                                        </td>
                                        <td>{c.quantity}</td>
                                        <td>{c.quantityUnits}</td>
                                        <td>{c.name}</td>
                                        <td>{c.description}</td>
                                        <td>{c.countryOfManufacture}</td>
                                        <td>{c.unitPrice.amount}</td>
                                        <td>{c.customsValue.amount}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </Table>
                    <div className='commodity-table-footer'>
                        <Button onClick={removeCommodity} disabled={selectedCommodityIds.length === 0} variant='secondary'>Delete</Button>
                        <div>
                            <p>Total customs value: {commodities.map((i) => i.customsValue.amount).reduce((accumulator, currValue) => accumulator + currValue, 0)}</p>
                            <p>Total commodities: {commodities.length}</p>
                            <p>Total shipment weight: {+commodities.map((i) => i.weight.value).reduce((accumulator, currValue) => accumulator + currValue, 0).toFixed(2)}</p>
                        </div>
                    </div>
                </FormSectionWrapper>
            </div>
        </div>
    )
}